import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Briefcase,
  Bug,
  Building2,
  CheckSquare,
  ClipboardList,
  HelpCircle,
  LogOut,
  MessageCircle,
  RotateCcw,
  Scale,
  Settings,
  Shield,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBusinessUnit } from "@/hooks/useBusinessUnit";
import { BusinessUnitIcon } from "@/components/business-unit/BusinessUnitIcon";
import { useRequests } from "@/hooks/useRequests";
import { useEndUserDestinations } from "@/components/layout/endUserDestinations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HELP_PAGE_OFFERED } from "@/components/help/helpAvailability";
import { ReportBugDialog } from "@/components/help/ReportBugDialog";

interface MenuLink {
  id: string;
  icon: LucideIcon;
  label: string;
  description: string | null;
  to?: string;
  /** Params to open `to` with.
   *
   *  "Waiting on you" holds two tabs, and a bare address opens the steps, so
   *  an entry offered because the *other* half has something has to name its
   *  tab (Jira NAV-111).
   *
   *  **An object replaces the search; a function edits it.** "Waiting on you"
   *  wants the first, because it names the tab to open and nothing else should
   *  survive. "Show the introduction" wants the second: the walkthrough is
   *  where `?redirect=` lives and this menu is on screen there, so replacing
   *  the search cost a new colleague the deep link they arrived with. */
  search?:
    | Record<string, string>
    | ((prev: Record<string, unknown>) => Record<string, unknown>);
  action?: () => void;
  badge?: number;
}

/**
 * The avatar menu: identity
 * header, role-dependent portal links (the portal
 * switcher), and the end-user pages that don't live in the top nav.
 *
 * This menu is in both shells — the top navbar and the admin header — so
 * anything an admin needs wherever they are stands here rather than in the
 * navbar, which the admin areas suppress.
 */
export function UserDropdown() {
  const {
    user,
    profile,
    role,
    businessUnitId,
    houseId,
    isImpersonating,
    onboardingCompleted,
    signOut,
  } = useAuth();
  const { businessUnit } = useBusinessUnit(businessUnitId);
  const { requests } = useRequests();
  // The shared destination list, for the one end-user entry this menu and the
  // navigation bar both offer. Reading it here rather than counting again is
  // what keeps the badge in the avatar menu and the badge in the bar the same
  // number — and this menu cannot simply be told: it is in both shells, and the
  // admin one renders no navigation bar to tell it.
  //
  // **The price, said out loud.** In the member shell the bar renders this menu,
  // so both call the hook and the two queues behind it are subscribed twice
  // where the step count alone used to be. Firestore shares a query target
  // between identical listeners, so the cost is local — two more callbacks, not
  // two more reads — and unlike the queue page (whose duplicate subscriptions
  // raced a route transition and left it on its skeleton, NAV-111) this menu is
  // mounted once and stays. A context would remove the duplicate; it is not
  // worth one number, and the moment it is, that is the change to make.
  const waitingOnYou = useEndUserDestinations().find(
    (destination) => destination.key === "waiting-on-you",
  );
  const { t } = useTranslation(["navigation", "common", "help"]);
  // Held here rather than inside the menu item: the dropdown unmounts its
  // content on select, so a dialog rendered inside it would close with it.
  const [reportBugOpen, setReportBugOpen] = useState(false);

  const pendingCount = requests.filter((r) => r.status === "PENDING").length;

  const isSuperAdmin = role === "super_admin";
  const isBuAdmin = role === "bu_admin" || isSuperAdmin;
  const isGroupLegal = role === "group_legal" || isSuperAdmin;

  // The Firestore profile name (kept current by the backend) wins over the
  // auth account's Google display name; a bare email beats nothing. The
  // terminal fallbacks are unreachable for a signed-in user (there is always
  // an email) but keep the header and avatar from rendering empty.
  const displayName =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") ||
    user?.displayName ||
    user?.email ||
    "User";
  const initials = (
    `${profile?.firstName?.[0] ?? ""}${profile?.lastName?.[0] ?? ""}` ||
    user?.displayName
      ?.split(" ")
      .map((part) => part[0])
      .slice(0, 2)
      .join("") ||
    user?.email?.[0] ||
    "U"
  ).toUpperCase();

  const portalLinks: MenuLink[] = [
    ...(isSuperAdmin
      ? [
          {
            id: "super-admin",
            icon: Shield,
            label: t("userDropdown.superAdminPortal"),
            description: t("userDropdown.groupWideManagement"),
            to: "/admin/dashboard",
          },
        ]
      : []),
    ...(isBuAdmin
      ? [
          {
            id: "bu-admin",
            icon: Building2,
            // One rail, two authorities (ADR 0025 decision 4). A BU admin who
            // belongs to a house administers that house, so calling their own
            // portal the business unit's names something they cannot reach —
            // the unit's entries, contracts and policies are all refused for
            // them, and the rail does not even offer them.
            label: t(
              houseId
                ? "userDropdown.houseAdminPortal"
                : "userDropdown.buAdminPortal",
            ),
            description: t(
              houseId
                ? "userDropdown.houseManagement"
                : "userDropdown.businessUnitManagement",
            ),
            to: "/bu-admin/dashboard",
          },
        ]
      : []),
    ...(isGroupLegal
      ? [
          {
            id: "group-legal",
            icon: Scale,
            label: t("userDropdown.groupLegalPortal"),
            description: t("userDropdown.groupContractManagement"),
            to: "/group-legal/dashboard",
          },
        ]
      : []),
  ];

  const endUserLinks: MenuLink[] = [
    {
      id: "toolkit",
      icon: Briefcase,
      label: t("userDropdown.myToolkit"),
      description: t("userDropdown.yourToolsAndRecent"),
      to: "/end-user/my-tools",
    },
    {
      id: "requests",
      icon: ClipboardList,
      label: t("userDropdown.myRequests"),
      description: t("userDropdown.trackPendingRequests"),
      to: "/end-user/my-requests",
      badge: pendingCount > 0 ? pendingCount : undefined,
    },
    // Only when something is actually waiting, badge and all — the source
    // gated this entry the same way (on its in-app approval stages, which
    // ADR 0004 removed; an evaluation step you are responsible for is what
    // replaced them). Unlike the source it is NOT hidden from BU admins: there
    // the admin queue made it redundant, but here the work is personally
    // theirs, and nobody else can close it for them.
    //
    // One entry over both halves since NAV-111, badge and gate included, from
    // the same list the top nav and the dashboard read — this menu is the only
    // way to it from an admin shell, which suppresses the navigation bar, so an
    // entry that counted only the steps would hide an access decision from the
    // one surface an admin can reach.
    ...(waitingOnYou
      ? [
          {
            id: "waiting-on-you",
            icon: CheckSquare,
            label: t(waitingOnYou.labelKey),
            description: t("userDropdown.stepsAndDecisions"),
            to: waitingOnYou.to,
            search: waitingOnYou.search,
            badge: waitingOnYou.badge,
          },
        ]
      : []),
    {
      id: "community",
      icon: MessageCircle,
      label: t("userDropdown.community"),
      description: t("userDropdown.tipsFromColleagues"),
      to: "/end-user/community",
    },
    // No Group Policies entry: policies are an admin-area concern;
    // /end-user/policies stays routable but unlinked.
  ];

  const preferenceLinks: MenuLink[] = [
    {
      id: "settings",
      icon: Settings,
      label: t("userDropdown.settings"),
      description: t("userDropdown.preferencesNotifications"),
      to: "/end-user/settings",
    },
    // Withdrawn until the help page's wording is agreed (NAV-114). Spread
    // rather than a `&&` inside the array, because a `false` in a `MenuLink[]`
    // is a type error and a filtered-out `null` is one more thing for the
    // renderer below to know about.
    ...(HELP_PAGE_OFFERED
      ? [
          {
            id: "help",
            icon: HelpCircle,
            label: t("userDropdown.helpSupport"),
            description: t("userDropdown.faqsAndContact"),
            to: "/help",
          } as MenuLink,
        ]
      : []),
    // **The way back into the walkthrough.** Finishing it is otherwise
    // one-way: a reader who pressed Skip on their first morning had no means
    // of changing their mind, and the reader most likely to skip is the one
    // with least time that morning. Offered to every role, because the gate
    // shows the walkthrough to every role.
    //
    // **Except while impersonating**, where the walkthrough has no exit. Skip
    // and Finish are both disabled there — they would write the *admin's* own
    // profile (PRD H5) — so offering the way in would strand somebody on a
    // screen with two dead buttons and the navigation bar as the only way out.
    //
    // `replay` is read by the root gate, which stands aside while it is in the
    // address; the walkthrough drops it when it finishes.
    //
    // **And only once the walkthrough is finished.** This menu is on screen on
    // `/onboarding`, so the entry was offered to somebody standing inside an
    // unfinished walkthrough — and pressing it there puts `?replay=true` on a
    // *first* run, which stops the bookmark being saved for the rest of it. A
    // new colleague who pressed it on screen two, paged on, and closed the tab
    // resumed on screen two, with the bookmark defeated on the one run it
    // exists for. There is no reason to offer a way back into a walkthrough
    // somebody is already in.
    ...(isImpersonating || !onboardingCompleted ? [] : [{
      id: "replay-onboarding",
      icon: RotateCcw,
      label: t("userDropdown.showIntroduction"),
      description: t("userDropdown.showIntroductionDescription"),
      to: "/onboarding",
      search: (prev) => ({ ...prev, replay: true }),
    } as MenuLink]),
    // This menu is the one control that is on screen in both shells, which is
    // what makes "reachable from every screen" (PRD I3) true for the admin
    // areas as well — they suppress the navigation bar.
    {
      id: "report-bug",
      icon: Bug,
      label: t("help:reportBug.title"),
      description: t("userDropdown.reportBugDescription"),
      action: () => setReportBugOpen(true),
    },
  ];

  const sessionLinks: MenuLink[] = [
    {
      id: "signout",
      icon: LogOut,
      label: t("common:signOut"),
      description: null,
      action: () => void signOut(),
    },
  ];

  const menuSections: MenuLink[][] = [
    portalLinks,
    endUserLinks,
    preferenceLinks,
    sessionLinks,
  ].filter((section) => section.length > 0);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* The visible content is just initials — the label carries the
              user's name for assistive tech (and the e2e login helpers). */}
          <button
            aria-label={displayName}
            className="w-9 h-9 rounded-full bg-gradient-navy text-white flex items-center justify-center text-sm font-semibold cursor-pointer transition-all duration-200 data-[state=open]:ring-2 data-[state=open]:ring-hb-navy"
            data-testid="button-user-dropdown"
          >
            {initials}
          </button>
        </DropdownMenuTrigger>
        {/* Bounded to the space actually below the trigger, and scrollable.
            This menu is the longest in the app — up to three portal links, four
            end-user pages, three preferences and sign-out, under a header — so on
            a 720px-high window its last item fell off the
            bottom, where `overflow-hidden` on the shared content simply clipped
            it. Sign out was the item that went (Jira NAV-75 added the eleventh
            row and tipped it over, but a super admin was already at the edge),
            and an unreachable sign-out is not a cosmetic loss.

            The `- 0.8rem` is not slack: the shared content carries `mt-[0.8rem]`
            as a hand-rolled gap, applied AFTER Popper measured how much room it
            had, so the height Popper offers is exactly that much too tall and
            the menu ends up hanging over the bottom edge by it. Subtracting it
            here fixes the arithmetic without changing `sideOffset` for every
            other dropdown in the app. */}
        <DropdownMenuContent
          align="end"
          className="w-72 p-0 shadow-dropdown max-h-[calc(var(--radix-dropdown-menu-content-available-height)_-_0.8rem)] overflow-y-auto"
        >
          <div className="p-5 border-b border-hb-grey/20 flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-md bg-gradient-navy text-white flex items-center justify-center text-base font-semibold">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-base font-semibold text-hb-navy-dark dark:text-white truncate">
                {displayName}
              </div>
              {/* The unit's own icon beside its own name (NAV-116). Only when
                  there is a unit to name: an icon with no name beside it would
                  be claiming something about a reader who belongs to no
                  business unit. */}
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <span className="truncate">
                  {t("userDropdown.employee")}
                  {businessUnit?.name ? ` · ${businessUnit.name}` : ""}
                </span>
                {businessUnit?.name && (
                  <BusinessUnitIcon
                    icon={businessUnit.icon}
                    className="h-3.5 w-3.5 shrink-0"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="p-2">
            {menuSections.map((section, index) => (
              <div key={section[0].id}>
                {index > 0 && <div className="h-px bg-hb-grey/20 my-2 mx-3" />}
                {section.map((item) => {
                  const content = (
                    <>
                      <item.icon className="w-5 h-5 shrink-0 text-hb-navy-light dark:text-hb-grey" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-hb-navy-dark dark:text-white">
                          {item.label}
                        </div>
                        {item.description && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {item.description}
                          </div>
                        )}
                      </div>
                      {item.badge !== undefined && (
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gradient-standard text-hb-navy"
                          data-testid={`dropdown-badge-${item.id}`}
                        >
                          {item.badge}
                        </span>
                      )}
                    </>
                  );
                  const itemClasses =
                    "flex items-center gap-3 px-3.5 py-3 rounded-md cursor-pointer";
                  return item.to ? (
                    <DropdownMenuItem key={item.id} asChild>
                      <Link
                        to={item.to}
                        search={item.search}
                        className={itemClasses}
                        data-testid={`dropdown-item-${item.id}`}
                      >
                        {content}
                      </Link>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      key={item.id}
                      className={itemClasses}
                      onSelect={item.action}
                      data-testid={`dropdown-item-${item.id}`}
                    >
                      {content}
                    </DropdownMenuItem>
                  );
                })}
              </div>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Outside the menu, because the menu unmounts its content when an item
          is selected and would take the dialog with it. */}
      <ReportBugDialog open={reportBugOpen} onOpenChange={setReportBugOpen} />
    </>
  );
}
